/* eslint-disable no-plusplus */
import { v4 as uuid } from "uuid";

type ExtraValues = {
  uuid?: string;
  valid?: boolean;
};

type ExtraValuesOptions = {
  [x in keyof ExtraValues]: boolean;
};

type GenericTemplate<T> = T & ExtraValues;

/** @deprecated Use `ColumnMappingCSV` instead for header-name-based mapping. */
export type ColumnPositionCSV<T> = Array<keyof GenericTemplate<T>>;

/**
 * Maps CSV header names (as they appear in the first row of the file)
 * to the corresponding property names on the target object type `T`.
 *
 * @example
 * const mapping: ColumnMappingCSV<CartonTemplateCSV> = {
 *   "PO Number": "poNumber",
 *   "Carton Reference": "cartonReference",
 *   "SKU": "sku",
 * };
 */
export type ColumnMappingCSV<T> = Record<string, keyof GenericTemplate<T>>;

// Explanation: https://regexr.com/44u6o
const READ_CSV_REGEX = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;

function readCSV<T>(
  data: unknown,
  columns: ColumnMappingCSV<T> | ColumnPositionCSV<T>,
  callbackOnSuccess?: (data: T[]) => void,
  insertExtraValues?: ExtraValuesOptions
) {
  if (!data || typeof data !== "string") return null;

  const rows = data.split("\n").map((row) => row.replace("\r", ""));
  const mounted: GenericTemplate<T>[] = [];

  if (rows && rows.length > 1) {
    const isMapping = !Array.isArray(columns);

    // Build a map of columnIndex -> property key
    const columnIndexToKey = new Map<number, keyof GenericTemplate<T>>();

    if (isMapping) {
      // New approach: read the header row and resolve positions dynamically
      const headerRow = rows[0];
      const headers = headerRow.split(READ_CSV_REGEX).map((h) => h.trim().replace(/"/g, ""));

      const mapping = columns as ColumnMappingCSV<T>;

      headers.forEach((header, idx) => {
        const propertyKey = mapping[header];
        if (propertyKey) {
          columnIndexToKey.set(idx, propertyKey);
        }
      });
    } else {
      // Legacy positional array: index in the array = column index
      const positionalColumns = columns as ColumnPositionCSV<T>;

      positionalColumns.forEach((col, idx) => {
        if (col) {
          columnIndexToKey.set(idx, col);
        }
      });
    }

    for (let index = 1, { length } = rows; index < length; index++) {
      const row = rows[index];

      if (!row) continue;

      const content = row.split(READ_CSV_REGEX);

      if (content) {
        const mountingObject = {} as GenericTemplate<T>;

        columnIndexToKey.forEach((propertyKey, colIndex) => {
          const value = content[colIndex]?.trim() as any;

          mountingObject[propertyKey] = value ? value.replace(/"/g, "") : "";
        });

        if (insertExtraValues?.uuid) mountingObject.uuid = uuid();
        if (insertExtraValues?.valid) mountingObject.valid = true;

        mounted.push(mountingObject);
      }
    }
  }

  if (callbackOnSuccess) return callbackOnSuccess(mounted);

  return mounted;
}

export default readCSV;
